import type { AiSelectedTitleInput, AiTitleOptionTarget } from "./types";

export const aiTitleOptionCsvHeaders = [
  "亿迈sku",
  "平台sku",
  "item_id",
  "旧标题",
  ...Array.from({ length: 10 }, (_, index) => `新标题${index + 1}`),
  "旧描述",
  "新描述",
] as const;

export interface AiTitleOptionCsvRow {
  target: AiTitleOptionTarget;
  titles?: readonly string[];
}

export interface ParsedSelectedTitles {
  selections: AiSelectedTitleInput[];
  skippedCount: number;
  errors: string[];
}

interface WritableFileLike {
  write(data: string): Promise<void>;
  close(): Promise<void>;
}

interface SaveFilePickerLike {
  (options: {
    suggestedName: string;
    types: Array<{ description: string; accept: Record<string, string[]> }>;
  }): Promise<{ createWritable(): Promise<WritableFileLike> }>;
}

export interface CsvSink {
  direct: boolean;
  write(value: string): Promise<void>;
  finish(): Promise<void>;
  closePartial(): Promise<void>;
}

function csvCell(value: unknown) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function serializeAiTitleOptionCsvRow(input: AiTitleOptionCsvRow) {
  return input.target.exportRows.map((row) => {
    const values: string[] = [row.yimaiSku, row.platformSku, row.itemId, row.oldTitle];
    for (let index = 0; index < 10; index += 1) values.push(input.titles?.[index] ?? "");
    values.push(row.oldDescription, row.newDescription);
    return `${values.map(csvCell).join(",")}\r\n`;
  }).join("");
}

export function aiTitleOptionCsvHeader() {
  return `\uFEFF${aiTitleOptionCsvHeaders.join(",")}\r\n`;
}

export async function createAiTitleOptionCsvSink(fileName: string): Promise<CsvSink> {
  const saveFilePicker = (window as Window & { showSaveFilePicker?: SaveFilePickerLike }).showSaveFilePicker;
  if (saveFilePicker) {
    const handle = await saveFilePicker({
      suggestedName: fileName,
      types: [{ description: "CSV 文件", accept: { "text/csv": [".csv"] } }],
    });
    const writable = await handle.createWritable();
    let closed = false;
    return {
      direct: true,
      write: (value) => writable.write(value),
      finish: async () => {
        if (closed) return;
        closed = true;
        await writable.close();
      },
      closePartial: async () => {
        if (closed) return;
        closed = true;
        await writable.close();
      },
    };
  }

  const chunks: string[] = [];
  let closed = false;
  return {
    direct: false,
    write: async (value) => { if (!closed) chunks.push(value); },
    finish: async () => {
      if (closed) return;
      closed = true;
      const href = URL.createObjectURL(new Blob(chunks, { type: "text/csv;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = fileName;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(href), 0);
    },
    closePartial: async () => {
      closed = true;
      chunks.length = 0;
    },
  };
}

function textValue(value: unknown) {
  return String(value ?? "").trim();
}

export function parseSelectedTitleRows(rows: readonly Record<string, unknown>[]): ParsedSelectedTitles {
  const selections: AiSelectedTitleInput[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();
  let skippedCount = 0;
  rows.forEach((row, index) => {
    const rowNo = index + 2;
    const selectedRaw = textValue(row.selected_no);
    if (!selectedRaw) {
      skippedCount += 1;
      return;
    }
    const selectedNo = Number(selectedRaw);
    if (!Number.isInteger(selectedNo) || selectedNo < 1 || selectedNo > 10) {
      errors.push(`第 ${rowNo} 行 selected_no 必须填写 1-10。`);
      return;
    }
    const candidateId = textValue(row.candidate_id);
    const sourceFingerprint = textValue(row.source_fingerprint).toLowerCase();
    const englishTitle = textValue(row[`title_option_${selectedNo}`]);
    if (!candidateId || !/^[a-f0-9]{64}$/.test(sourceFingerprint)) {
      errors.push(`第 ${rowNo} 行候选 ID 或源资料指纹无效。`);
      return;
    }
    const length = Array.from(englishTitle).length;
    if (length < 48 || length > 60) {
      errors.push(`第 ${rowNo} 行所选英文标题为 ${length} 字符，必须为 48-60 字符。`);
      return;
    }
    if (seen.has(candidateId)) {
      errors.push(`第 ${rowNo} 行 candidate_id 重复。`);
      return;
    }
    seen.add(candidateId);
    selections.push({ candidateId, sourceFingerprint, englishTitle });
  });
  return { selections, skippedCount, errors };
}
